import React, { useEffect, useRef, useState } from 'react'
import { cn } from '../lib/utils'

// Import car logo components
import { AudiIcon } from './car-logos/audi'
import { BMWIcon } from './car-logos/bmw'
import { FordIcon } from './car-logos/ford'
import { HondaIcon } from './car-logos/honda'
import { HyundaiIcon } from './car-logos/hyundai'
import { JeepIcon } from './car-logos/jeep'
import { KiaIcon } from './car-logos/kia'
import { MercedesBenzIcon } from './car-logos/mercedes-benz'
import { MiniIcon } from './car-logos/mini'
import { NissanIcon } from './car-logos/nissan'
import { PorscheIcon } from './car-logos/porsche'
import { SubaruIcon } from './car-logos/subaru'
import { TeslaIcon } from './car-logos/tesla'
import { ToyotaIcon } from './car-logos/toyota'
import { VolkswagenIcon } from './car-logos/volkswagen'
import { VolvoIcon } from './car-logos/volvo'

// Constants
const CAR_LOGO_WIDTH_MOBILE = '80px' // Smaller for mobile to show 4+ brands
const CAR_LOGO_WIDTH_DESKTOP = '160px'
const CLONE_SETS_COUNT = 2

interface LogoData {
  id: string
  icon: React.ReactElement
}

// Logo icons mapping with proper sizing
const logoIcons: Record<string, React.ReactElement> = {
  kia: <KiaIcon style={{ height: '10px', flexShrink: 0 }} />,
  subaru: <SubaruIcon style={{ height: '15px', flexShrink: 0 }} />,
  mini: <MiniIcon style={{ height: '24px', flexShrink: 0 }} />,
  hyundai: <HyundaiIcon style={{ height: '12px', flexShrink: 0 }} />,
  mercedesBenz: <MercedesBenzIcon style={{ height: '24px', flexShrink: 0 }} />,
  toyota: <ToyotaIcon style={{ height: '17px', flexShrink: 0 }} />,
  bmw: <BMWIcon style={{ height: '28px', flexShrink: 0 }} />,
  honda: <HondaIcon style={{ height: '12px', flexShrink: 0 }} />,
  audi: <AudiIcon style={{ height: '21px', flexShrink: 0 }} />,
  volvo: <VolvoIcon style={{ height: '11px', flexShrink: 0 }} />,
  volkswagen: <VolkswagenIcon style={{ height: '25px', flexShrink: 0 }} />,
  porsche: <PorscheIcon style={{ height: '8px', flexShrink: 0 }} />,
  nissan: <NissanIcon style={{ height: '26px', flexShrink: 0 }} />,
  tesla: <TeslaIcon style={{ height: '10px', flexShrink: 0 }} />,
  jeep: <JeepIcon style={{ height: '18px', flexShrink: 0 }} />,
  ford: <FordIcon style={{ height: '29px', flexShrink: 0 }} />,
}

const initialLogos: LogoData[] = Object.entries(logoIcons).map(
  ([id, icon]) => ({ id, icon })
)

function createClonedLogos(logos: LogoData[], totalClones: number): LogoData[] {
  return Array.from({ length: totalClones }).flatMap((_, index) =>
    logos.map(({ id, icon }) => ({
      id: `${id}-clone-${index + 1}`,
      icon,
    }))
  )
}

// Utility function to set CSS variables
function setCSSVariable(name: string, value: string) {
  document.documentElement.style.setProperty(name, value)
}

const BrandsSlider: React.FC = () => {
  const logosListRef = useRef<HTMLDivElement>(null)
  const [clonedLogos, setClonedLogos] = useState<LogoData[]>([])
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    const logoList = logosListRef.current

    if (logoList) {
      const totalLogos = initialLogos.length

      // Function to update CSS variables based on screen size
      const updateLogoWidth = () => {
        const isMobile = window.innerWidth < 768
        const logoWidth = isMobile ? CAR_LOGO_WIDTH_MOBILE : CAR_LOGO_WIDTH_DESKTOP

        // Set CSS variables for the animation
        setCSSVariable('--slider-total-clones', (CLONE_SETS_COUNT + 1).toString())
        setCSSVariable('--slider-total-logos', totalLogos.toString())
        setCSSVariable('--slider-logo-width', logoWidth)
        setCSSVariable(
          '--slider-total-logo-width',
          `calc(var(--slider-total-logos) * var(--slider-logo-width) * var(--slider-total-clones))`
        )
      }

      // Initial setup
      updateLogoWidth()
      setClonedLogos(createClonedLogos(initialLogos, CLONE_SETS_COUNT))
      setIsVisible(true)

      // Update on resize
      window.addEventListener('resize', updateLogoWidth)
      return () => window.removeEventListener('resize', updateLogoWidth)
    }
  }, [])

  return (
    <div className="py-1 sm:py-2 md:py-4 lg:py-6 w-full">
      <div className="relative h-10 min-h-10 overflow-x-hidden">
        {/* Left gradient fade */}
        <div className="before:absolute before:left-0 before:top-0 before:z-10 before:h-full before:w-[30%] before:bg-gradient-to-r before:from-gray-50 dark:before:from-background before:content-['']"></div>
        
        {/* Right gradient fade */}
        <div className="after:absolute after:right-0 after:top-0 after:z-10 after:h-full after:w-[30%] after:bg-gradient-to-l after:from-gray-50 dark:after:from-background after:content-['']"></div>
        
        <div className="flex h-full items-center justify-center">
          <div 
            ref={logosListRef} 
            className="animate-slider"
            style={{
              // Inline styles for better iOS Safari compatibility
              display: 'flex',
              width: 'var(--slider-total-logo-width)',
            }}
          >
            <div
              className={cn(
                'flex items-center transition-opacity duration-300',
                // Light mode: make logos black
                '[&_svg]:brightness-0 [&_svg]:saturate-100',
                // Dark mode: make logos gray
                'dark:[&_svg]:brightness-0 dark:[&_svg]:saturate-100 dark:[&_svg]:invert dark:[&_svg]:opacity-60',
                isVisible ? 'opacity-100' : 'opacity-0'
              )}
              style={{
                // Ensure proper width calculation for iOS
                width: '100%',
                flexShrink: 0,
              }}
            >
              {initialLogos.concat(clonedLogos).map(({ id, icon }) => (
                <div
                  key={id}
                  className="mx-2 md:mx-5 inline-flex items-center justify-center"
                  style={{
                    width: 'var(--slider-logo-width)',
                    flexShrink: 0,
                  }}
                >
                  {icon}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BrandsSlider
